import {
  BufferGeometry,
  Mesh,
  MeshStandardMaterial,
  Scene,
  BufferAttribute,
  DoubleSide,
  Texture,
  Color,
} from "three";
import { extendShaderWithWorldGradient } from "../utils/shader_utils";
import { CausticMaterial } from "../materials/causetic_material";
import AssetManager from "../utils/asset_manager";
import SceneManager from "../scene_mgr";


class Pool {
  pool: Mesh;
  poolCausticMesh: Mesh;
  poolLength: number;
  poolWidth: number;
  poolDepth: number;
  color: number = 0xffffff;
  causticMaterial: CausticMaterial;
  material:MeshStandardMaterial;
  sceneMgr:SceneManager;
  isInEditMode:boolean = false;

  constructor(poolLength: number, poolWidth: number, poolDepth: number, sceneMgr:SceneManager) {
    this.sceneMgr = sceneMgr;
    this.poolLength = poolLength;
    this.poolWidth = poolWidth;
    this.poolDepth = poolDepth;

    const tex = (
      AssetManager.getInstance().getAsset("causticMap") as Texture
    ).clone();
    tex.repeat.set(8, 8);

    this.causticMaterial = new CausticMaterial(
      tex,
      new Color(0x000000),
      new Color(0x558888),
      0.6,
      true,
      0.9,
      2.0,
    );

     

    this.createPoolMesh();
  }

  addToScene(scene: Scene) {
    scene.add(this.pool);
    scene.add(this.poolCausticMesh);
  }

  removeFromScene(scene: Scene) {
    scene.remove(this.pool);
    scene.remove(this.poolCausticMesh);
  }

  resize(
    poolLength: number,
    poolWidth: number,
    poolDepth: number,
    scene: Scene
  ) {
    this.poolLength = poolLength;
    this.poolWidth = poolWidth;
    this.poolDepth = poolDepth;

    this.removeFromScene(scene);
    this.createPoolMesh();
    this.addToScene(scene);
  }

  updateColor(newColor: number, scene: Scene) {
    this.color = newColor;
    this.removeFromScene(scene);
    this.createPoolMesh();
    this.addToScene(scene);
  }

  private createPoolMesh() {
    //console.log("Creating pool mesh");

    let poolDepth = this.poolDepth;
    if (this.sceneMgr.poolType == 'infinity') {
    //  poolDepth+= this.sceneMgr.margelleHeight
    }

    const geometry = new BufferGeometry();

    this.material = new MeshStandardMaterial({
      color: this.color,
      metalness: 0.0,
      roughness: 1.0,
      wireframe: this.isInEditMode ? true : false,
      side: DoubleSide,
    });

    // Define vertices (excluding top face)
    const halfLength = this.poolLength / 2;
    const halfWidth = this.poolWidth / 2;
    const vertices = new Float32Array([
      // Bottom face
      -halfLength,
      -halfWidth,
      0,
      halfLength,
      -halfWidth,
      0,
      halfLength,
      halfWidth,
      0,
      -halfLength,
      halfWidth,
      0,

      // Side faces
      -halfLength,
      -halfWidth,
      0,
      halfLength,
      -halfWidth,
      0,
      halfLength,
      -halfWidth,
      this.poolDepth,
      -halfLength,
      -halfWidth,
      this.poolDepth,
      -halfLength,
      halfWidth,
      0,
      halfLength,
      halfWidth,
      0,
      halfLength,
      halfWidth,
      this.poolDepth,
      -halfLength,
      halfWidth,
      this.poolDepth,
      -halfLength,
      -halfWidth,
      0,
      -halfLength,
      halfWidth,
      0,
      -halfLength,
      halfWidth,
      this.poolDepth,
      -halfLength,
      -halfWidth,
      this.poolDepth,
      halfLength,
      -halfWidth,
      0,
      halfLength,
      halfWidth,
      0,
      halfLength,
      halfWidth,
      this.poolDepth,
      halfLength,
      -halfWidth,
      this.poolDepth,
    ]);

    // Define indices
    const indices = new Uint32Array([
      // Bottom face
      0, 1, 2, 0, 2, 3,
      // Side faces
      4, 5, 6, 4, 6, 7, 8, 9, 10, 8, 10, 11, 12, 13, 14, 12, 14, 15, 16, 17, 18,
      16, 18, 19,
    ]);

    // Define UVs (planar mapping aligned with each face)
    const uvs = new Float32Array([
      // Bottom face
      0, 0, 1, 0, 1, 1, 0, 1,
      // Side faces (repeated for each side)
      0, 0, 1, 0, 1, 1, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 0,
      0, 1, 0, 1, 1, 0, 1,
    ]);

    geometry.setIndex(new BufferAttribute(indices, 1));
    geometry.setAttribute("position", new BufferAttribute(vertices, 3));
    geometry.setAttribute("uv", new BufferAttribute(uvs, 2));

    //  UVHelpers.autoUV({ geometry:geometry });

    geometry.computeVertexNormals();
   // geometry.computeTangents();

    

    extendShaderWithWorldGradient(this.material, 0.0);

    this.pool = new Mesh(geometry, this.material);
    this.pool.receiveShadow = true;
    this.pool.castShadow = true;
    this.pool.renderOrder = 1;

    this.pool.rotation.x = -Math.PI / 2;

    let offset = 0.0;
    if (this.sceneMgr.poolType == 'infinity') {
      offset = this.sceneMgr.margelleHeight;
    }
    this.pool.translateZ(-this.poolDepth + offset);
    


    if (this.isInEditMode) return;

    // now create the caustic mesh that follow the pool bottom face

    const causticGeometry = new BufferGeometry();
    const causticVertices = new Float32Array([
      // Bottom face
      -halfLength,
      -halfWidth,
      0,
      halfLength,
      -halfWidth,
      0,
      halfLength,
      halfWidth,
      0,
      -halfLength,
      halfWidth,
      0,
    ]);

    // Define indices
    const causticIndices = new Uint32Array([0, 1, 2, 0, 2, 3]);
    const causticsUVs = new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]);

    causticGeometry.setAttribute(
      "position",
      new BufferAttribute(causticVertices, 3)
    );

    causticGeometry.setAttribute("uv", new BufferAttribute(causticsUVs, 2));

    causticGeometry.setIndex(new BufferAttribute(causticIndices, 1));

    causticGeometry.computeVertexNormals();
    //causticGeometry.computeTangents();

    this.poolCausticMesh = new Mesh(causticGeometry, this.causticMaterial);
    this.poolCausticMesh.rotateX(-Math.PI / 2);
    this.poolCausticMesh.translateZ(-this.poolDepth + offset + 0.01);
  }

  setTimeOfDay(timeOfDay: string) {
    if (timeOfDay == "day") {
      this.poolCausticMesh.visible=true;
    } else {
      this.poolCausticMesh.visible=false;
    }
  }

  accuTime: number = 0;
  updateTime(delta: number) {
    this.accuTime += 0.1;
    this.causticMaterial.updateTime(this.accuTime);
  }

  setEditMode(isEditMode: boolean, scene:Scene) {
    this.isInEditMode = isEditMode;

    if (this.isInEditMode) {
      if (this.poolCausticMesh!=null) {
        this.poolCausticMesh.visible=false;
      }
    }
    this.resize(this.poolLength, this.poolWidth, this.poolDepth, scene);
  }
}

export default Pool;
